import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { baseApi } from './services/BaseService';
import { shareApi } from './services/DashboardShareService';
import auth from './slices/authSlice';
import share from './slices/shareSlice';
import profiles from './slices/profilesSlice';
import companies from './slices/companiesSlice';
import tags from './slices/tagsSlice';
import deliveries from './slices/deliveriesSlice';
import usages from './slices/usagesSlice';
import templates from './slices/templatesSlice';
import emailNotifications from './slices/emailNotificationsSlice';
import pricingPlans from './slices/pricingPlansSlice';
import signupRequests from './slices/signupRequestsSlice';
import deletionRequests from './slices/deletionRequestsSlice';
import textLabel from './slices/textLabelSlice';
import tagsTranslations from './slices/tagsTranslationsSlice';
import integrationDefinitions from './slices/integrationDefinitionSlice';
import mediaLibraryTags from './slices/mediaLibraryTagsSlice';
import { clearAdminData } from '../shared/access/access';
import { accessMiddleware } from '../shared/access/accessMiddleware';
import type { AnyAction } from '@reduxjs/toolkit';

const combinedReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  [shareApi.reducerPath]: shareApi.reducer,
  auth,
  share,
  profiles,
  companies,
  tags,
  mediaLibraryTags,
  deliveries,
  usages,
  templates,
  emailNotifications,
  pricingPlans,
  signupRequests,
  deletionRequests,
  textLabel,
  integrationDefinitions,
  tagsTranslations,
});

const rootReducer = (state: ReturnType<typeof combinedReducer> | undefined, action: AnyAction): ReturnType<typeof combinedReducer> => {
  if (state && clearAdminData.match(action)) {
    const fresh = combinedReducer(undefined, { type: '@@access/reset' });
    state = { ...fresh, auth: state.auth, share: state.share, [shareApi.reducerPath]: state[shareApi.reducerPath] };
  }
  return combinedReducer(state, action);
};

export const setupStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(accessMiddleware, baseApi.middleware, shareApi.middleware),
  });
};

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
